import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const FEES_ACCESS_KEY = "asha_os_fees_er235_access";

const STYLES = `
.pms-quote-doc {
  --paper: #fcfcfd;
  --paper-2: #f4f5f8;
  --ink: #1c2540;
  --ink-2: #404a66;
  --ink-3: #737d96;
  --line: #d8dde6;
  --line-2: #e7eaf0;
  --accent: #3a5bd9;
  --accent-deep: #2638b2;
  --accent-soft: #eef0fa;
  --accent-ink: #2c43c4;
  --accent-soft-2: #eaedf7;
  --accent-border: #bdc6e8;

  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 14px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-feature-settings: 'ss01', 'cv11';
}

.pms-quote-doc *,
.pms-quote-doc *::before,
.pms-quote-doc *::after { box-sizing: border-box; }

.pms-quote-doc .page {
  max-width: 1040px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(0,0,0,0.02),
    0 30px 60px -30px rgba(20,30,40,0.18),
    0 12px 24px -16px rgba(20,30,40,0.10);
  overflow: hidden;
}

.pms-quote-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.pms-quote-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .brand-mark {
  width: 28px; height: 28px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
}
.pms-quote-doc .brand-mark img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.pms-quote-doc .brand-meta {
  color: var(--ink-3);
  font-weight: 450;
}
.pms-quote-doc .topbar-right {
  display: flex;
  gap: 12px;
  align-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-3);
}
.pms-quote-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.pms-quote-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(238, 240, 250, 0.7);
}

.pms-quote-doc .hero {
  padding: 72px 56px 56px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(1200px 320px at 90% -100px, rgba(216, 222, 248, 0.55), transparent 60%),
    var(--paper);
}
.pms-quote-doc .eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 18px;
}
.pms-quote-doc .eyebrow b { color: var(--accent-ink); font-weight: 500; }
.pms-quote-doc h1.title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.035em;
  margin: 0 0 14px;
  color: var(--ink);
  max-width: 18ch;
}
.pms-quote-doc .title-sub {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 24px;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
  margin: 0 0 26px;
}
.pms-quote-doc .lede {
  font-size: 17px;
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 62ch;
  margin: 0 0 48px;
  text-wrap: pretty;
}

.pms-quote-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  margin-top: 8px;
}
.pms-quote-doc .meta-foot {
  margin: 14px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-3);
  font-style: italic;
}
.pms-quote-doc .meta-cell {
  padding: 18px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .meta-cell:first-child { padding-right: 36px; }
.pms-quote-doc .meta-cell:not(:first-child) { padding-left: 36px; padding-right: 36px; }
.pms-quote-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.pms-quote-doc .meta-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
}
.pms-quote-doc .meta-value {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .meta-value.placeholder {
  color: var(--ink-3);
  font-weight: 400;
}

.pms-quote-doc section.s {
  padding: 64px 56px;
  border-bottom: 1px solid var(--line);
}
.pms-quote-doc section.s:last-of-type { border-bottom: 0; }
.pms-quote-doc .s-head {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  margin-bottom: 36px;
  align-items: baseline;
}
.pms-quote-doc .s-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding-top: 10px;
  border-top: 1px solid var(--ink);
  width: 52px;
}
.pms-quote-doc .s-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 34px;
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .s-sub {
  font-size: 15px;
  color: var(--ink-2);
  margin-top: 10px;
  max-width: 60ch;
  text-wrap: pretty;
}
.pms-quote-doc .s-sub b { color: var(--ink); font-weight: 500; }
.pms-quote-doc .s-body {
  margin-left: 152px;
}

.pms-quote-doc .incl-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 0;
  border-top: 1px solid var(--line);
}
.pms-quote-doc .incl-col {
  padding: 28px 32px 28px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .incl-col + .incl-col {
  padding-left: 32px;
  padding-right: 0;
  border-right: 0;
}
.pms-quote-doc .incl-col-head {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
  row-gap: 2px;
  align-items: center;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc .incl-count {
  grid-row: 1 / span 2;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 38px;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--ink);
}
.pms-quote-doc .incl-count.accent { color: var(--accent); }
.pms-quote-doc .incl-col-title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
  align-self: end;
}
.pms-quote-doc .incl-col-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  align-self: start;
}

.pms-quote-doc .prod-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pms-quote-doc .prod-list li {
  padding-bottom: 14px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .prod-list li:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .prod-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pms-quote-doc .prod-sub {
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.5;
}
.pms-quote-doc .soon {
  display: inline-flex;
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 9.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 500;
}

.pms-quote-doc .agent-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  counter-reset: agent;
}
.pms-quote-doc .agent-list li {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 13.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  padding: 11px 14px 11px 28px;
  border-bottom: 1px solid var(--line-2);
  position: relative;
  counter-increment: agent;
}
.pms-quote-doc .agent-list li::before {
  content: counter(agent, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 12px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--ink-3);
  font-weight: 400;
  letter-spacing: 0.04em;
}
.pms-quote-doc .agent-list li:nth-last-child(-n+2) { border-bottom: 0; }
.pms-quote-doc .agent-list li:nth-child(odd) { padding-right: 18px; }

.pms-quote-doc .piecemeal-line {
  font-family: 'Inter', sans-serif;
  font-size: 19px;
  line-height: 1.6;
  color: var(--ink-2);
  margin: 0 0 28px;
  padding: 0 0 18px;
  border-bottom: 1px solid var(--line);
}
.pms-quote-doc .piecemeal-line b {
  color: var(--ink);
  font-weight: 600;
}
.pms-quote-doc .piecemeal-line .pml-meta {
  color: var(--ink-3);
  font-size: 16px;
  display: inline;
}

.pms-quote-doc .vendor-s {
  background: linear-gradient(180deg, var(--paper) 0%, #f7f7fb 100%);
}
.pms-quote-doc .vendor-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.pms-quote-doc .vendor-table thead th {
  text-align: left;
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.pms-quote-doc .vendor-table thead th.vt-price { text-align: right; padding-right: 0; }
.pms-quote-doc .vendor-table tbody td {
  padding: 16px 0;
  border-bottom: 1px solid var(--line-2);
  vertical-align: top;
}
.pms-quote-doc .vendor-table .vt-cat { width: 38%; padding-right: 24px; }
.pms-quote-doc .vendor-table .vt-vendor { width: 36%; padding-right: 24px; color: var(--ink-2); font-size: 13px; }
.pms-quote-doc .vendor-table .vt-price {
  width: 26%;
  text-align: right;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 17px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
}
.pms-quote-doc .vendor-table .vt-price small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}
.pms-quote-doc .vendor-table .vt-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
}
.pms-quote-doc .vendor-table .vt-meta {
  font-size: 11.5px;
  color: var(--ink-3);
  margin-top: 3px;
  line-height: 1.45;
}
.pms-quote-doc .vendor-table tr.muted .vt-cat .vt-name { color: var(--ink-3); font-weight: 450; }
.pms-quote-doc .vendor-table tr.muted .vt-price { color: var(--ink-3); font-weight: 400; font-size: 14px; }
.pms-quote-doc .vendor-table tr:last-child td { border-bottom: 0; }

.pms-quote-doc .vendor-foot {
  margin-top: 18px;
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.55;
  max-width: 80ch;
}

.pms-quote-doc .price-primer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  align-items: stretch;
  margin-bottom: 36px;
}
.pms-quote-doc .prim-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 26px 28px;
  background: var(--paper);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pms-quote-doc .prim-card.accent {
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
  border-color: var(--accent-border);
}
.pms-quote-doc .prim-tag {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.pms-quote-doc .prim-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .prim-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 36px;
  letter-spacing: -0.025em;
  color: var(--ink);
  line-height: 1;
  margin-top: 4px;
}
.pms-quote-doc .prim-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 6px;
  letter-spacing: 0;
}
.pms-quote-doc .prim-amount .strike {
  display: inline-block;
  font-size: 18px;
  font-weight: 400;
  color: var(--ink-3);
  text-decoration: line-through;
  margin-right: 10px;
  letter-spacing: -0.01em;
}
.pms-quote-doc .prim-desc {
  font-size: 12.5px;
  color: var(--ink-2);
  line-height: 1.55;
  margin-top: 6px;
}
.pms-quote-doc .prim-desc b { color: var(--ink); font-weight: 500; }
.pms-quote-doc .prim-plus {
  display: grid;
  place-items: center;
  width: 56px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 300;
  font-size: 28px;
  color: var(--ink-3);
}

.pms-quote-doc .tier-mini-head {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 10px;
  padding-top: 4px;
}

/* Volume tier comparison table */
.pms-quote-doc .aif-table {
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  background: var(--paper);
}
.pms-quote-doc .aif-row {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr;
  align-items: center;
  padding: 16px 22px;
  border-bottom: 1px solid var(--line-2);
  gap: 24px;
}
.pms-quote-doc .aif-row:last-child { border-bottom: 0; }
.pms-quote-doc .aif-row.head {
  padding-top: 14px; padding-bottom: 14px;
  background: var(--paper-2);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.pms-quote-doc .aif-row.accent-row {
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.5));
}
.pms-quote-doc .aif-band {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .aif-band-sub {
  font-size: 12px;
  color: var(--ink-3);
  margin-top: 2px;
}
.pms-quote-doc .aif-fee {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 19px;
  letter-spacing: -0.02em;
  color: var(--ink);
}
.pms-quote-doc .aif-fee small {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-left: 4px;
  letter-spacing: 0;
}
.pms-quote-doc .aif-fee.struck {
  color: var(--ink-3);
  text-decoration: line-through;
  font-weight: 400;
  font-size: 16px;
}
.pms-quote-doc .aif-fee.accent {
  color: var(--accent-ink);
}
.pms-quote-doc .aif-col-h {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
}
.pms-quote-doc .aif-col-h.accent { color: var(--accent-ink); }

.pms-quote-doc .fineprint {
  list-style: none;
  padding: 22px 0 0;
  margin: 32px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid var(--line-2);
}
.pms-quote-doc .fineprint li {
  font-size: 12.5px;
  color: var(--ink-3);
  line-height: 1.6;
  max-width: 88ch;
  padding-left: 18px;
  position: relative;
}
.pms-quote-doc .fineprint li::before {
  content: "";
  position: absolute;
  left: 0; top: 9px;
  width: 4px; height: 4px;
  border-radius: 99px;
  background: var(--ink-3);
}
.pms-quote-doc .fineprint .fp-label {
  color: var(--ink);
  font-weight: 500;
  font-family: 'Inter Tight', sans-serif;
  margin-right: 4px;
}
.pms-quote-doc .fineprint b { color: var(--ink); font-weight: 500; }

.pms-quote-doc table.lines {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.pms-quote-doc table.lines th,
.pms-quote-doc table.lines td {
  text-align: left;
  padding: 22px 0;
  vertical-align: top;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc table.lines thead th {
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.pms-quote-doc table.lines tbody tr:last-child td { border-bottom: 0; }
.pms-quote-doc .li-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin-bottom: 6px;
}
.pms-quote-doc .li-desc {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 52ch;
}
.pms-quote-doc .li-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: var(--ink);
  white-space: nowrap;
}
.pms-quote-doc .li-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}
.pms-quote-doc .li-note {
  font-size: 12.5px;
  color: var(--ink-2);
  line-height: 1.5;
}
.pms-quote-doc .col-item { width: 40%; padding-right: 32px !important; }
.pms-quote-doc .col-billing { width: 26%; padding-right: 32px !important; }
.pms-quote-doc .col-amount { width: 22%; text-align: right !important; padding-right: 32px !important; }
.pms-quote-doc .col-notes { width: 34%; padding-left: 0 !important; }
.pms-quote-doc td.col-amount,
.pms-quote-doc th.col-amount { text-align: right !important; }

.pms-quote-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
}

/* FAQ section */
.pms-quote-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  border-top: 1px solid var(--line);
  padding-top: 28px;
}
.pms-quote-doc .faq-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  padding-bottom: 22px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .faq-q {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 18px;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .faq-a {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-2);
  max-width: 80ch;
  margin: 0;
}
.pms-quote-doc .faq-a b { color: var(--ink); font-weight: 500; }

@media (max-width: 900px) {
  .pms-quote-doc .page { margin: 24px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail { padding-left: 28px; padding-right: 28px; }
  .pms-quote-doc h1.title { font-size: 44px; }
  .pms-quote-doc .s-head { grid-template-columns: 1fr; gap: 12px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .pms-quote-doc .incl-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .incl-col { padding: 24px 0; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .price-primer { grid-template-columns: 1fr; }
  .pms-quote-doc .prim-plus { width: auto; padding: 8px 0; }
}

@media (max-width: 720px) {
  /* Tighter outer chrome */
  .pms-quote-doc .page { margin: 14px; border-radius: 14px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail {
    padding-left: 20px; padding-right: 20px;
  }
  .pms-quote-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .pms-quote-doc .topbar-right { gap: 8px; }
  .pms-quote-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .pms-quote-doc section.s { padding-top: 40px; padding-bottom: 40px; }

  /* Hero scales down */
  .pms-quote-doc h1.title { font-size: 34px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 18px; margin-bottom: 20px; }
  .pms-quote-doc .lede { font-size: 15px; margin-bottom: 32px; }

  /* Single-column meta grid with horizontal dividers */
  .pms-quote-doc .meta-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .meta-cell {
    padding: 14px 0 !important;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .meta-cell:last-child { border-bottom: 0; }

  /* Stack the .lines table (Service / [Billing] / Amount [/ Notes]) into cards */
  .pms-quote-doc table.lines,
  .pms-quote-doc table.lines tbody,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc table.lines td {
    display: block;
    width: auto !important;
  }
  .pms-quote-doc table.lines thead { display: none; }
  .pms-quote-doc table.lines tr {
    padding: 22px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc table.lines tbody tr:first-child { padding-top: 18px; }
  .pms-quote-doc table.lines tbody tr:last-child { border-bottom: 0; }
  .pms-quote-doc table.lines td {
    padding: 0 !important;
    border: 0 !important;
  }
  .pms-quote-doc table.lines td + td { margin-top: 14px; }
  .pms-quote-doc table.lines td.col-amount { text-align: left !important; }
  .pms-quote-doc .li-desc,
  .pms-quote-doc .li-note { max-width: none; }

  /* Stack the vendor-table into cards as well */
  .pms-quote-doc .vendor-table,
  .pms-quote-doc .vendor-table tbody,
  .pms-quote-doc .vendor-table tr,
  .pms-quote-doc .vendor-table td {
    display: block;
    width: auto !important;
  }
  .pms-quote-doc .vendor-table thead { display: none; }
  .pms-quote-doc .vendor-table tbody tr {
    padding: 18px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .vendor-table tbody tr:last-child { border-bottom: 0; }
  .pms-quote-doc .vendor-table tbody td {
    padding: 0 !important;
    border: 0 !important;
  }
  .pms-quote-doc .vendor-table tbody td + td { margin-top: 8px; }
  .pms-quote-doc .vendor-table .vt-price { text-align: left; }

  /* Volume tier compare table — stack to two columns under header */
  .pms-quote-doc .aif-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 14px 16px;
    align-items: start;
  }
  .pms-quote-doc .aif-row > div:first-child {
    grid-column: 1 / -1;
  }
  .pms-quote-doc .aif-row.head {
    grid-template-columns: 1fr 1fr;
  }
  .pms-quote-doc .aif-row.head > div:first-child {
    display: none;
  }
  .pms-quote-doc .aif-fee { font-size: 17px; }

  /* Footer wraps cleanly instead of squeezing 3 spans onto one line */
  .pms-quote-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }

}

@media print {
  @page {
    size: letter;
    margin: 0.3in;
  }
  html, body {
    background: #fff !important;
  }
  body.pms-quote-body { background: #fff !important; }
  .pms-quote-doc { font-size: 11px !important; }
  .pms-quote-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .pms-quote-doc .topbar { padding: 14px 24px; }
  .pms-quote-doc .hero { padding: 32px 24px 24px; }
  .pms-quote-doc h1.title { font-size: 32px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 16px; margin-bottom: 14px; }
  .pms-quote-doc .lede { margin-bottom: 20px; font-size: 12px; }
  .pms-quote-doc section.s { padding: 24px; }
  .pms-quote-doc .s-head {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 16px;
  }
  .pms-quote-doc .s-title { font-size: 22px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .tail { padding: 12px 24px; }

  .pms-quote-doc .meta-grid { grid-template-columns: repeat(2, 1fr); }
  .pms-quote-doc .incl-grid { display: block; }
  .pms-quote-doc .incl-col {
    padding: 18px 0;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .incl-col:last-child { border-bottom: 0; }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .agent-list { grid-template-columns: 1fr 1fr 1fr; }

  .pms-quote-doc .meta-grid,
  .pms-quote-doc .prim-card,
  .pms-quote-doc .aif-row,
  .pms-quote-doc .vendor-table tr,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc .fineprint li,
  .pms-quote-doc .prod-list li,
  .pms-quote-doc .agent-list li {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .pms-quote-doc h1.title,
  .pms-quote-doc .s-title,
  .pms-quote-doc .prod-name,
  .pms-quote-doc .li-name,
  .pms-quote-doc .vt-name,
  .pms-quote-doc .incl-col-head,
  .pms-quote-doc .s-head {
    break-after: avoid;
    page-break-after: avoid;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

const OsFeesEr235Page = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (hasStoredContentAccess(FEES_ACCESS_KEY)) setIsLocked(false);
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FEES_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Fee schedule access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const link1 = document.createElement("link");
    link1.rel = "preconnect";
    link1.href = "https://fonts.googleapis.com";

    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "";

    const link3 = document.createElement("link");
    link3.rel = "stylesheet";
    link3.href =
      "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Inter:wght@400;450;500;600&family=JetBrains+Mono:wght@400;500&family=Fraunces:opsz,wght@9..144,300;9..144,400&display=swap";

    document.head.appendChild(link1);
    document.head.appendChild(link2);
    document.head.appendChild(link3);

    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#f1efe7";
    document.body.style.margin = "0";
    document.body.classList.add("pms-quote-body");
    document.title = "Asha — Fee Schedule";

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(link3);
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("pms-quote-body");
    };
  }, []);

  return (
    <div className="pms-quote-doc">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {isLocked && <EmailGate onAccessGranted={handleAccessGranted} />}

      <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <div className="topbar-right">
            <span className="pill">Custom Quote</span>
            <span className="pill">Confidential</span>
          </div>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="eyebrow">
            <b>Fee Schedule</b> &nbsp;·&nbsp; <b>Multi-Office</b> Custom Pricing
          </div>
          <h1 className="title">
            The AI Operating System for Modern Dental Offices
          </h1>
          <div className="title-sub">
            Custom pricing for 2 locations — contingent on signing both
          </div>
          <p className="lede">
            This fee schedule outlines pricing for Asha's full front-office
            and back-office AI agent suite plus our software layer, delivered
            via continuous integration into your existing PMS — your PMS and
            ePrescribe stay where they are. Each office has its own AI
            Agents Fee rate based on its collections profile:{" "}
            <b style={{ color: "var(--ink)", fontWeight: 500 }}>flat 1.5%</b>{" "}
            for the high-volume office ($400k+ collections) and{" "}
            <b style={{ color: "var(--ink)", fontWeight: 500 }}>flat 3.0%</b>{" "}
            for the smaller office ($100k collections) — both negotiated
            concessions from Asha's standard 3.5% pricing.{" "}
            <b style={{ color: "var(--ink)", fontWeight: 500 }}>
              These rates are contingent on signing both offices.
            </b>{" "}
            Final terms are governed by the executed Statement of Work and
            the Asha Terms of Service.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">Asha Health, Inc.</div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Fees valid through</div>
              <div className="meta-value">Q2 2026</div>
            </div>
          </div>
          <p className="meta-foot">
            Fees subject to an increase for offices signing up after Q2 2026.
          </p>
        </div>

        {/* § 01 — INCLUDED */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 01</div>
            <div>
              <h2 className="s-title">What's included</h2>
              <p className="s-sub">
                Every location plan ships with our full AI agent suite plus
                8 softwares that layer on top of your existing PMS via
                continuous integration. PMS and ePrescribe are{" "}
                <b>not included</b> under this model — your office continues
                to run those on your current vendor.
              </p>
            </div>
          </div>
          <div className="s-body">
            <div className="incl-grid">
              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count">8</span>
                  <span className="incl-col-title">Softwares</span>
                  <span className="incl-col-sub">
                    Layered on top of your existing PMS via continuous
                    integration.
                  </span>
                </div>
                <ul className="prod-list">
                  <li>
                    <div className="prod-name">Communication Suite</div>
                    <div className="prod-sub">
                      VoIP phone system · Yealink desk phones · 2-way texting ·
                      Universal inbox · Team chat &amp; channels · Smart voice
                      logs · Intelligent fax · Next-gen digital forms
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Marketing Suite</div>
                    <div className="prod-sub">
                      SMS &amp; email campaigns · Automated workflows ·
                      Clinically-aware segmentation · AI handles patient
                      replies
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">eClaims Portal</div>
                    <div className="prod-sub">
                      Built-in eClaims · Clearinghouse connectivity · Single
                      login · Integrated submission
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Revenue Intelligence Suite</div>
                    <div className="prod-sub">
                      Reporting &amp; analytics dashboards · KPI tracking ·
                      Collection-rate insights · Revenue cycle analytics ·
                      Performance metrics
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      Insurance Verification Software
                    </div>
                    <div className="prod-sub">
                      Automated verification · Real-time eligibility · Benefits
                      breakdown upload · Dual-source verification
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Integrated Payments Suite</div>
                    <div className="prod-sub">
                      Text-to-pay · Card readers · Multi-method support ·
                      Automated reminders
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Patient Portal</div>
                    <div className="prod-sub">
                      Secure patient links · Interactive forms · Treatment-plan
                      viewing · Insurance info upload
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Online Scheduling</div>
                    <div className="prod-sub">
                      Patient self-scheduling · Real-time availability
                    </div>
                  </li>
                </ul>
              </div>

              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count accent">20</span>
                  <span className="incl-col-title">AI Agents</span>
                  <span className="incl-col-sub">
                    Front- and back-office automation, end-to-end.
                  </span>
                </div>
                <ul className="agent-list">
                  <li>AI Receptionist</li>
                  <li>AI Schedule Optimizer</li>
                  <li>AI Hygiene Recall</li>
                  <li>AI Patient Experience</li>
                  <li>AI Case Acceptance</li>
                  <li>AI Patient Intake</li>
                  <li>AI Practice Manager</li>
                  <li>AI Fax Manager</li>
                  <li>AI Insurance Verification</li>
                  <li>AI Claim Submission</li>
                  <li>AI Claim Scrubbing</li>
                  <li>AI Denial Management</li>
                  <li>AI Patient Billing</li>
                  <li>AI AR &amp; Collections</li>
                  <li>AI EOB Posting</li>
                  <li>AI Insurance Calls</li>
                  <li>AI Copilot</li>
                  <li>AI Scribe</li>
                  <li>AI Treatment Planner</li>
                  <li>AI Revenue Analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* § 02 — PRICING COMPARISON */}
        <section className="s vendor-s">
          <div className="s-head">
            <div className="s-num">§ 02</div>
            <div>
              <h2 className="s-title">Pricing comparison</h2>
              <p className="s-sub">
                If you tried to assemble what Asha gives you out of the box
                from other vendors, here's what it would actually cost. Using
                Asha replaces this entire stack with a single platform — saving
                your practice significant cost every month.
              </p>
            </div>
          </div>

          <div className="s-body">
            <p className="piecemeal-line">
              <b>
                If you piecemealed this from other vendors, you would pay
                {" "}<span>$7,800</span> +{" "}
                <span>3.5% of collections</span>
              </b>{" "}
              per location, per month...on the low end —{" "}
              <span className="pml-meta">
                on top of your existing PMS &amp; ePrescribe.
              </span>
            </p>

            <table className="vendor-table">
              <thead>
                <tr>
                  <th className="vt-cat">Capability</th>
                  <th className="vt-vendor">Vendors</th>
                  <th className="vt-price">Typical price</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      Communication Suite (premium)
                    </div>
                  </td>
                  <td className="vt-vendor">Weave · RevenueWell</td>
                  <td className="vt-price">
                    $700 – $1,200<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Revenue Analytics</div>
                  </td>
                  <td className="vt-vendor">Dental Intel</td>
                  <td className="vt-price">
                    $300 – $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      Insurance Verification (software)
                    </div>
                  </td>
                  <td className="vt-vendor">Zuub · Airpay</td>
                  <td className="vt-price">
                    $300 – $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">eClaims Portal</div>
                  </td>
                  <td className="vt-vendor">VyneTrellis · DentalExchange</td>
                  <td className="vt-price">
                    $150 – $250<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">AI Scribe</div>
                  </td>
                  <td className="vt-vendor">
                    Typical scribe vendors · ~$149 / provider
                  </td>
                  <td className="vt-price">
                    ~$300<small>/ mo (2 providers)</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">AI Receptionist</div>
                  </td>
                  <td className="vt-vendor">Dentina · Arini</td>
                  <td className="vt-price">
                    $300 – $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">AI Case Acceptance</div>
                  </td>
                  <td className="vt-vendor">Codent</td>
                  <td className="vt-price">
                    $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      AI Schedule Optimizer &amp; Outbound Calls
                    </div>
                  </td>
                  <td className="vt-vendor">PatientDesk</td>
                  <td className="vt-price">
                    $1,000<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      AI Hygiene Recall &amp; Outbound Calls
                    </div>
                  </td>
                  <td className="vt-vendor">Patient Desk</td>
                  <td className="vt-price">
                    $1,000<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Insurance Billing Service</div>
                    <div className="vt-meta">
                      Submission, scrubbing, denials, posting
                    </div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    3.5%<small>of collections</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Insurance Verification Calls</div>
                    <div className="vt-meta">
                      Based on $8/patient × 300 patients/mo
                    </div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    $2,400<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Payment Reconciliation</div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    $200<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Patient Billing</div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    $1,000<small>/ mo</small>
                  </td>
                </tr>
                <tr className="muted">
                  <td className="vt-cat">
                    <div className="vt-name">AI EOB Posting</div>
                    <div className="vt-meta">
                      Shown separately — not added to total to avoid
                      double-counting against the 3.5% insurance billing line
                      above.
                    </div>
                  </td>
                  <td className="vt-vendor">Lassie</td>
                  <td className="vt-price">
                    2%<small>of collections</small>
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="vendor-foot">
              Figures reflect a representative subset of capabilities Asha
              includes — the full Asha platform covers more. Ranges are typical
              published rack rates and may vary by practice size, region, and
              contract length.
            </p>
          </div>
        </section>

        {/* § 03 — OUR PRICING */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 03</div>
            <div>
              <h2 className="s-title">Our pricing for you</h2>
              <p className="s-sub">
                This is a multi-office quote spanning <b>2 locations</b>.
                Each office has its own flat AI Agents Fee rate based on
                its collections profile, layered on top of the standard{" "}
                <b>$800</b> per-location Platform Fee.{" "}
                <b>Pricing is contingent on signing both offices.</b>
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="tier-mini-head">
              Office A — high volume ($400k+ collections)
            </div>
            <div className="price-primer">
              <div className="prim-card">
                <div className="prim-tag">Platform Fee</div>
                <div className="prim-name">Asha Platform</div>
                <div className="prim-amount">
                  $800<small>/ month</small>
                </div>
                <div className="prim-desc">
                  A fixed monthly fee covering the 8 softwares we provide
                  (communication suite, marketing suite, revenue intelligence
                  suite, eClaims, payments, &amp; more) plus continuous
                  integration into your existing PMS.
                </div>
              </div>
              <div className="prim-plus">+</div>
              <div className="prim-card accent">
                <div className="prim-tag">AI Agents Fee (flat)</div>
                <div className="prim-name">Office A</div>
                <div className="prim-amount">
                  <span className="strike">3.5%</span>
                  1.5%<small>of collections · flat, no tiers</small>
                </div>
                <div className="prim-desc">
                  A negotiated <b>flat 1.5%</b> rate reflecting Office A's
                  high collection volume ($400k+). Calculated against the
                  greater of actual monthly collections or the{" "}
                  <b>$400,000</b> floor.
                </div>
              </div>
            </div>

            <div className="tier-mini-head" style={{ marginTop: 32 }}>
              Office B — $100k collections
            </div>
            <div className="price-primer">
              <div className="prim-card">
                <div className="prim-tag">Platform Fee</div>
                <div className="prim-name">Asha Platform</div>
                <div className="prim-amount">
                  $800<small>/ month</small>
                </div>
                <div className="prim-desc">
                  A fixed monthly fee covering the 8 softwares we provide
                  (communication suite, marketing suite, revenue intelligence
                  suite, eClaims, payments, &amp; more) plus continuous
                  integration into your existing PMS.
                </div>
              </div>
              <div className="prim-plus">+</div>
              <div className="prim-card accent">
                <div className="prim-tag">AI Agents Fee (flat)</div>
                <div className="prim-name">Office B</div>
                <div className="prim-amount">
                  <span className="strike">3.5%</span>
                  3.0%<small>of collections · flat, no tiers</small>
                </div>
                <div className="prim-desc">
                  A negotiated <b>flat 3.0%</b> rate for Office B.
                  Calculated against the greater of actual monthly
                  collections or the <b>$100,000</b> floor.
                </div>
              </div>
            </div>

            <ul className="fineprint">
              <li>
                <span className="fp-label">Multi-office contingency.</span>
                This pricing is{" "}
                <b>contingent on signing both offices under one engagement</b>.
                If only one office is signed, standard pricing (3.5% with
                $135,000 floor) applies.
              </li>
              <li>
                <span className="fp-label">Monthly Retainer — Office A.</span>
                On the first day of each billing month, Office A pays a{" "}
                <b>$6,800</b> Monthly Retainer — the{" "}
                <b>$800 Platform Fee</b> plus <b>$6,000</b> toward the AI
                Agents Fee (1.5% × $400,000 floor). At month-end, Asha
                calculates the actual AI Agents Fee on actual collections;
                any amount above $6,000 is invoiced as a true-up.
              </li>
              <li>
                <span className="fp-label">Monthly Retainer — Office B.</span>
                Office B pays a <b>$3,800</b> Monthly Retainer — the{" "}
                <b>$800 Platform Fee</b> plus <b>$3,000</b> toward the AI
                Agents Fee (3.0% × $100,000 floor). True-up applies the same
                way.
              </li>
              <li>
                <span className="fp-label">AI Credits.</span>
                150,000 credits per location are included each month — most
                practices stay well within this allotment. We specify AI credit
                limits because running AI models is expensive and uncapped
                usage would risk us losing money on contracts. Overages bill at{" "}
                <b>$0.023 per 1,000 credits</b> (2.3¢ per 1K) per location.
                Your account manager can share utilization-management
                techniques &amp; tools to help your team stay within the
                included allotment.
              </li>
            </ul>
          </div>
        </section>

        {/* § 04 — CONCESSIONS & TERMS */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 04</div>
            <div>
              <h2 className="s-title">Concessions &amp; discounts</h2>
              <p className="s-sub">
                Negotiated departures from Asha's standard pricing and
                contracting terms, applied to this quote.
              </p>
            </div>
          </div>

          <div className="s-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="col-item">Item</th>
                  <th className="col-billing">Standard</th>
                  <th className="col-amount">Your quote</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="col-item">
                    <div className="li-name">AI Agents Fee — Office A</div>
                    <div className="li-desc">
                      Rate applied to Office A's collections each billing
                      month ($400k+ volume).
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-amount">
                      3.5%<small>of collections</small>
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      Flat 1.5%<small>of collections</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">AI Agents Fee — Office B</div>
                    <div className="li-desc">
                      Rate applied to Office B's collections each billing
                      month ($100k volume).
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-amount">
                      3.5%<small>of collections</small>
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      Flat 3.0%<small>of collections</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Onboarding Fee</div>
                    <div className="li-desc">
                      One-time setup, data migration, configuration &amp;
                      training. Applied per office.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-amount">
                      $1,800<small>per office</small>
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      Waived<small>$0 at signing · both offices</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Continuous Integration Fee</div>
                    <div className="li-desc">
                      Monthly fee covering bidirectional integration with
                      your existing PMS. Applied per office.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-amount">
                      $400<small>per office / month</small>
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      Waived<small>$0 / month · both offices</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Performance opt-out</div>
                    <div className="li-desc">
                      Right to exit if Services do not substantially conform
                      to baseline functionality after a re-evaluation period.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-amount">
                      None<small>not included</small>
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      6-month opt-out<small>included</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Cancellation liability</div>
                    <div className="li-desc">
                      Amounts forfeited or owed upon cancellation prior to end
                      of term.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-amount">
                      Full clawback<small>Retainer + ETF + Discount/Concessions</small>
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      Retainer only<small>no other clawback</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Multi-office contingency</div>
                    <div className="li-desc">
                      Concessions above are conditioned on signing both
                      offices under one engagement.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-amount">
                      Independent<small>per-office pricing</small>
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      Both offices required<small>or standard pricing applies</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* § 05 — ONE-TIME */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 05</div>
            <div>
              <h2 className="s-title">One-time fees &amp; deposit</h2>
              <p className="s-sub">
                Charged once per location at signing.
              </p>
            </div>
          </div>

          <div className="s-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="col-item">Item</th>
                  <th className="col-amount">Amount</th>
                  <th className="col-notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Onboarding Fee — Office A</div>
                    <div className="li-desc">
                      One-time setup, data migration, configuration &amp;
                      training for Office A.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      <span style={{ textDecoration: "line-through", color: "var(--ink-3)", fontWeight: 400, marginRight: 8 }}>$1,800</span>
                      Waived
                      <small>$0 at signing</small>
                    </div>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      Negotiated concession. Full white-glove implementation
                      still included.
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Onboarding Fee — Office B</div>
                    <div className="li-desc">
                      One-time setup, data migration, configuration &amp;
                      training for Office B.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      <span style={{ textDecoration: "line-through", color: "var(--ink-3)", fontWeight: 400, marginRight: 8 }}>$1,800</span>
                      Waived
                      <small>$0 at signing</small>
                    </div>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      Negotiated concession. Full white-glove implementation
                      still included.
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Deposit — Office A</div>
                    <div className="li-desc">
                      Equal to Office A's Monthly Retainer. Secures your
                      onboarding start date and is applied to the final
                      month of the contract term.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $6,800<small>Office A</small>
                    </div>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      Collected upon signing. Refundable in the form of a
                      credit against the final month's invoice.
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Deposit — Office B</div>
                    <div className="li-desc">
                      Equal to Office B's Monthly Retainer. Secures your
                      onboarding start date and is applied to the final
                      month of the contract term.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $3,800<small>Office B</small>
                    </div>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      Collected upon signing. Refundable in the form of a
                      credit against the final month's invoice.
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* § 06 — FAQ */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 06</div>
            <div>
              <h2 className="s-title">Frequently asked questions</h2>
              <p className="s-sub">
                Quick answers to the most common questions about how this fee
                schedule applies to your practice.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="faq-list">
              <div className="faq-item">
                <h3 className="faq-q">How are locations determined?</h3>
                <p className="faq-a">
                  Locations follow exactly how your existing practice
                  management system has them configured. Asha maps your data
                  over one-to-one during migration, so{" "}
                  <b>locations cannot be merged or separated</b> as part of
                  onboarding — whatever structure exists in your current PMS
                  is what we mirror in Asha.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  How do the AI Agents Fee rates work across offices?
                </h3>
                <p className="faq-a">
                  Each office has its own flat AI Agents Fee rate.{" "}
                  <b>Office A</b> (high volume, $400k+ collections) is{" "}
                  <b>flat 1.5%</b>, calculated against the greater of
                  actual collections or the <b>$400,000</b> floor.{" "}
                  <b>Office B</b> ($100k collections) is <b>flat 3.0%</b>,
                  calculated against the greater of actual collections or
                  the <b>$100,000</b> floor. Both rates are concessions
                  from Asha's standard <b>3.5%</b> pricing.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  Why are the rates different between offices?
                </h3>
                <p className="faq-a">
                  Higher collection volumes deliver stronger unit economics,
                  so Office A's larger scale earns a sharper rate.
                  Office B's rate sits at <b>3.0%</b> — still a concession
                  from our standard <b>3.5%</b>. Both rates are{" "}
                  <b>contingent on signing both offices</b> under one
                  engagement; if only one office is signed, standard
                  pricing applies.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What's waived under this quote?
                </h3>
                <p className="faq-a">
                  Three line items are waived for both offices: (a) the{" "}
                  <b>$1,800</b> Onboarding Fee per office, (b) the{" "}
                  <b>$400 / month</b> Continuous Integration fee per
                  office, and (c) the standard Discount Clawback,
                  Concessions Clawback, and Early Termination Fee on
                  cancellation. Only the Monthly Retainer for the
                  cancellation month is forfeited.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What happens if we want to cancel?
                </h3>
                <p className="faq-a">
                  This quote includes a <b>6-month performance-based
                  opt-out</b>: if the Services don't substantially conform
                  to baseline functionality after a re-evaluation period,
                  you may exit the contract with no early termination
                  liability beyond fees already accrued. If you cancel for
                  any other reason during the term, the only forfeiture is
                  the <b>Monthly Retainer for the cancellation month</b> —
                  there is no Early Termination Fee, no Discount Clawback,
                  and no Concessions Clawback under this quote.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>
            Confidential — Summary of proposed commercial terms; not a binding
            agreement.
          </span>
          <span>Page 01 / 01</span>
        </div>
      </div>

    </div>
  );
};

export default OsFeesEr235Page;
